import React from "react";
import { TextField, MenuItem, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { LocalizationProvider, DesktopDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import MinimizeOutlinedIcon from "@mui/icons-material/MinimizeOutlined";
import { useAppSelector } from "domain/hooks/redux-hook";
import { formatDate } from "utils/formatDate";
import FormModalContainer from "presentation/components/FormModalContainer";

type RequestFormProps = {
    isOpen?: boolean;
    handleClose?: () => void;
    handleReset?: () => void;
    onSubmit?: (...input: any[]) => void;
};

const FilterForm: React.FC<RequestFormProps> = ({
    isOpen = false,
    handleClose = () => {},
    handleReset = () => {},
    onSubmit = () => {},
}) => {
    const { t } = useTranslation();

    const { pettyCashType, pettyCashAccountabilityStatus, pettyCashStatus } =
        useAppSelector((state) => state.main.disi);

    const { control, register, setValue, handleSubmit } = useFormContext();

    return (
        <FormModalContainer
            header="Filter"
            subHeader={t("disi.petty_cash.filter_subheader") || ""}
            open={isOpen}
            onClose={handleClose}
            handleSubmit={handleSubmit}
            applyButton={onSubmit}
            cancelButton={handleReset}
            applyText={t("disi.partner_deposit.filter_submit") as string}
            cancelText={t("disi.partner_deposit.filter_cancel") as string}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Controller
                        name="fromDate"
                        control={control}
                        render={({ field: { value, name } }) => (
                            <DesktopDatePicker
                                label={t("disi.petty_cash.filter_start_date")}
                                inputFormat="DD-MM-YYYY"
                                disableFuture
                                value={value === "" ? null : value}
                                onChange={(val) => {
                                    const date = dayjs(val).toDate();
                                    setValue(
                                        name,
                                        formatDate(date, "MM-DD-YYYY")
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        onChange={(e) => e.target.blur()}
                                        onKeyDown={(e) => e.preventDefault()}
                                    />
                                )}
                            />
                        )}
                    />
                    <MinimizeOutlinedIcon
                        sx={{
                            color: "rgba(0, 0, 0, 0.54)",
                            m: "1rem",
                        }}
                    />
                    <Controller
                        name="toDate"
                        control={control}
                        render={({ field: { value, name } }) => (
                            <DesktopDatePicker
                                label={t("disi.petty_cash.filter_end_date")}
                                mask=""
                                inputFormat="DD-MM-YYYY"
                                disableFuture
                                value={value === "" ? null : value}
                                onChange={(val) => {
                                    const date = dayjs(val).toDate();
                                    setValue(
                                        name,
                                        formatDate(date, "MM-DD-YYYY")
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        onChange={(e) => e.target.blur()}
                                        onKeyDown={(e) => e.preventDefault()}
                                    />
                                )}
                            />
                        )}
                    />
                </Stack>
            </LocalizationProvider>
            <TextField
                {...register("pettyCashId")}
                label={t("disi.petty_cash.filter_petty_id")}
                fullWidth
            />
            <Controller
                control={control}
                name="tipePettyCash"
                render={({ field: { onChange, value } }) => (
                    <TextField
                        select
                        value={value}
                        onChange={onChange}
                        label={t("disi.petty_cash.filter_petty_type")}
                    >
                        {pettyCashType.data.map((item) => {
                            return (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                )}
            />
            <Controller
                control={control}
                name="status"
                render={({ field: { onChange, value } }) => (
                    <TextField
                        select
                        value={value}
                        onChange={onChange}
                        label={t("disi.petty_cash.filter_petty_status")}
                    >
                        <MenuItem value="ALL">ALL</MenuItem>
                        {pettyCashStatus.data.map((item) => {
                            return (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                )}
            />
            <Controller
                control={control}
                name="accountabilityStatus"
                render={({ field: { onChange, value } }) => (
                    <TextField
                        select
                        value={value}
                        onChange={onChange}
                        label={t(
                            "disi.petty_cash.filter_petty_accountability_status"
                        )}
                    >
                        <MenuItem value="ALL">ALL</MenuItem>
                        {pettyCashAccountabilityStatus.data.map((item) => {
                            return (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                )}
            />
        </FormModalContainer>
    );
};

export default FilterForm;
